import React, { useState, useEffect, useMemo } from 'react';
import { Heart } from 'lucide-react';
import { getUserLikes } from '@/services/likes.api';
import VideoTable from '@/components/table/VideoTable';
import { analyzeVideo } from '@/services/audio.api';
import { VideoFilterBar, useVideoFilters } from '@/components/video-filters';
import { useAppDispatch } from '@/store/store';
import { addToQueue, setQueue, playVideo } from '@/features/player/playerSlice';
import { Button, Select } from '@/components/ui';
import AddToCratePopover from '@/features/crates/AddToCratePopover';

interface LikedVideo {
  video: {
    id: string;
    videoId: string;
    title: string;
    description?: string;
    thumbnailUrl?: string;
    publishedAt: string;
    channel: {
      id: string;
      channelId: string;
      name: string;
      thumbnailUrl?: string;
    };
  };
  likedAt: string;
  audioFeatures?: Record<string, number> | null;
}

type DateRange = 'all' | 'week' | 'month' | 'year';

const PAGE_SIZE = 100;

const LikedVideos: React.FC = () => {
  const [items, setItems] = useState<LikedVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [crateTarget, setCrateTarget] = useState<{ videoId: string; title: string } | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      try {
        const all: LikedVideo[] = [];
        const seen = new Set<string>();
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const response = await getUserLikes({ limit: PAGE_SIZE, offset });
          for (const item of response.likes as LikedVideo[]) {
            if (!seen.has(item.video.videoId)) {
              seen.add(item.video.videoId);
              all.push(item);
            }
          }
          hasMore = response.pagination.hasMore;
          offset += PAGE_SIZE;
        }

        if (!cancelled) setItems(all);
      } catch (error) {
        if (!cancelled) console.error('Failed to load liked videos', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  const dateFilteredItems = useMemo(() => {
    if (dateRange === 'all') return items;
    const cutoff = new Date();
    if (dateRange === 'week') cutoff.setDate(cutoff.getDate() - 7);
    if (dateRange === 'month') cutoff.setMonth(cutoff.getMonth() - 1);
    if (dateRange === 'year') cutoff.setFullYear(cutoff.getFullYear() - 1);
    return items.filter(item => new Date(item.likedAt) >= cutoff);
  }, [items, dateRange]);

  const likedAtByVideoId = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach(item => map.set(item.video.videoId, new Date(item.likedAt).getTime()));
    return map;
  }, [items]);

  const channelByVideoId = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach(item => map.set(item.video.videoId, item.video.channel?.name ?? ''));
    return map;
  }, [items]);

  const videos = useMemo(
    () =>
      dateFilteredItems.map(item => ({
        ...item.video,
        isLiked: true,
        channelId: item.video.channel?.channelId || item.video.channel?.id || '',
        publishedAt: new Date(item.video.publishedAt),
        dateAdded: new Date(item.likedAt),
        createdAt: new Date(item.video.publishedAt),
        updatedAt: new Date(item.video.publishedAt),
      })) as any[],
    [dateFilteredItems]
  );

  const audioFeatures = useMemo(() => {
    const map: Record<string, any> = {};
    dateFilteredItems.forEach(item => {
      if (item.audioFeatures) map[item.video.videoId] = item.audioFeatures;
    });
    return map;
  }, [dateFilteredItems]);

  const channels = useMemo(() => {
    const byId = new Map<string, any>();
    items.forEach(item => {
      const channel = item.video.channel;
      if (channel?.channelId && !byId.has(channel.channelId)) byId.set(channel.channelId, channel);
    });
    return Array.from(byId.values());
  }, [items]);

  const filterState = useVideoFilters(videos, audioFeatures, {
    options: [
      { value: 'likedRecent', label: 'Recently Liked' },
      { value: 'likedOldest', label: 'First Liked' },
      { value: 'channel', label: 'Channel A-Z' },
    ],
    compare: (a, b, sortBy) => {
      if (sortBy === 'likedRecent')
        return (likedAtByVideoId.get(b.videoId) ?? 0) - (likedAtByVideoId.get(a.videoId) ?? 0);
      if (sortBy === 'likedOldest')
        return (likedAtByVideoId.get(a.videoId) ?? 0) - (likedAtByVideoId.get(b.videoId) ?? 0);
      if (sortBy === 'channel')
        return (channelByVideoId.get(a.videoId) ?? '').localeCompare(channelByVideoId.get(b.videoId) ?? '');
      return null;
    },
  });

  useEffect(() => {
    filterState.setSortBy('likedRecent');
  }, []);

  const handlePlayVideo = (video: any) => {
    dispatch(setQueue(filterState.filteredVideos));
    dispatch(playVideo(video));
  };

  const handleAddToQueue = (video: any) => {
    dispatch(addToQueue(video));
  };

  const handleAnalyze = async (video: any) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  const clearEverything = () => {
    filterState.resetFilters();
    setDateRange('all');
  };

  const hasActiveFilters =
    dateRange !== 'all' ||
    filterState.filters.searchQuery !== '' ||
    filterState.filters.channelId !== '';

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-300 dark:bg-bg-dark-tertiary rounded w-48 mb-6" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-neutral-200 dark:bg-bg-dark-secondary rounded-xl h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 pt-4 md:p-6 max-w-7xl mx-auto">
      <h1 className="px-4 md:px-0 text-3xl font-bold text-meradio-dark dark:text-text-dark-primary mb-6">
        My Collection
      </h1>

      <VideoFilterBar
        {...filterState}
        channels={channels}
        sortOptions={[
          { value: 'likedRecent', label: 'Recently Liked' },
          { value: 'likedOldest', label: 'First Liked' },
          { value: 'channel', label: 'Channel A-Z' },
        ]}
        totalCount={items.length}
      >
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-1">
            Liked
          </label>
          <Select value={dateRange} onChange={(e) => setDateRange(e.target.value as DateRange)}>
            <option value="all">All Time</option>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
            <option value="year">Past Year</option>
          </Select>
        </div>
        <div className="flex items-end">
          <Button variant="link" size="sm" onClick={clearEverything}>
            Clear All
          </Button>
        </div>
      </VideoFilterBar>

      {filterState.filteredVideos.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="w-16 h-16 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-600 dark:text-text-dark-primary mb-2">
            No liked videos found
          </h3>
          <p className="text-neutral-500 dark:text-text-dark-secondary mb-6">
            {hasActiveFilters
              ? 'Try adjusting your filters to see more results.'
              : 'Start liking videos to build your collection!'}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearEverything}>
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <VideoTable
          videos={filterState.filteredVideos}
          audioFeatures={audioFeatures}
          onPlayVideo={handlePlayVideo}
          onAddToQueue={handleAddToQueue}
          onAnalyze={handleAnalyze}
          onAddToCrate={(video) => setCrateTarget({ videoId: video.videoId, title: video.title })}
        />
      )}

      <AddToCratePopover
        videoId={crateTarget?.videoId ?? null}
        videoTitle={crateTarget?.title}
        open={crateTarget !== null}
        onClose={() => setCrateTarget(null)}
      />
    </div>
  );
};

export default LikedVideos;
